/**
 * PhoneUpload — the mobile capture page at `/upload/phone?token=<tok>`.
 *
 * Supports multi-page uploads: the user can photograph one page at a time
 * and add as many pages as needed before tapping "Hotovo". The QR upload
 * token stays valid until its TTL (10 min) so subsequent POST /upload calls
 * with the same token all succeed.
 */
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Camera, CheckCircle2, FileText, Loader2, Plus, XCircle } from 'lucide-react'

import { usePhoneUpload, usePhoneUploadFinish } from '@/hooks/queries'
import { ApiError } from '@/lib/api'

type PageState = {
  previewUrl: string
  isPdf: boolean
  status: 'uploading' | 'done' | 'error'
  errorMessage?: string
}

export default function PhoneUpload() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [pages, setPages] = useState<PageState[]>([])
  const [finished, setFinished] = useState(false)
  const upload = usePhoneUpload(token)
  const finish = usePhoneUploadFinish(token)

  const isUploading = pages.some((p) => p.status === 'uploading')
  const hasDone = pages.some((p) => p.status === 'done')
  const tokenDead = pages.some((p) => p.errorMessage === 'expired')

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    const isPdf = file.type === 'application/pdf'
    const previewUrl = isPdf ? '' : URL.createObjectURL(file)
    // Capture index before the state update so callbacks reference the right slot.
    const idx = pages.length
    setPages((prev) => [...prev, { previewUrl, isPdf, status: 'uploading' }])
    upload.mutate(file, {
      onSuccess: () =>
        setPages((prev) =>
          prev.map((p, i) => (i === idx ? { ...p, status: 'done' } : p)),
        ),
      onError: (err) => {
        const expired = err instanceof ApiError && err.status === 410
        setPages((prev) =>
          prev.map((p, i) =>
            i === idx
              ? { ...p, status: 'error', errorMessage: expired ? 'expired' : 'error' }
              : p,
          ),
        )
      },
    })
  }

  if (!token) {
    return (
      <PhoneFrame>
        <StatusBlock
          icon={<XCircle className="h-12 w-12 text-pop-fire" strokeWidth={2.5} />}
          title="Chybí kód"
          message="Tahle stránka se otevírá naskenováním QR kódu na obrazovce počítače."
        />
      </PhoneFrame>
    )
  }

  if (finished) {
    return (
      <PhoneFrame>
        <StatusBlock
          icon={<CheckCircle2 className="h-12 w-12 text-pop-covered" strokeWidth={2.5} />}
          title="Hotovo!"
          message={`Nahráno ${pages.filter((p) => p.status === 'done').length} ${pageLabel(pages.filter((p) => p.status === 'done').length)}. Vrať se k obrazovce počítače — smlouva se tam za okamžik objeví.`}
        />
      </PhoneFrame>
    )
  }

  return (
    <PhoneFrame>
      <h1 className="text-2xl font-semibold uppercase tracking-wide text-pop-silhouette text-center">
        Vyfoť smlouvu
      </h1>
      <p className="text-sm text-pop-shadow text-center max-w-xs">
        {pages.length === 0
          ? 'Vyfoť celou stránku pojistné smlouvy. Postará se o zbytek.'
          : 'Přidej další stranu nebo ukonči nahrávání.'}
      </p>

      {/* Uploaded pages thumbnails */}
      {pages.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center w-full max-w-xs">
          {pages.map((page, i) => (
            <div key={i} className="relative w-20 h-20">
              {page.isPdf ? (
                <div className="w-full h-full flex items-center justify-center border-[2px] border-pop-silhouette bg-white">
                  <FileText className="h-10 w-10 text-pop-shadow" strokeWidth={1.5} />
                </div>
              ) : (
                <img
                  src={page.previewUrl}
                  alt={`Strana ${i + 1}`}
                  className="w-full h-full object-cover border-[2px] border-pop-silhouette"
                />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                {page.status === 'uploading' && (
                  <Loader2 className="h-6 w-6 text-pop-fire animate-spin" strokeWidth={3} />
                )}
                {page.status === 'done' && (
                  <CheckCircle2 className="h-6 w-6 text-pop-covered drop-shadow" strokeWidth={2.5} />
                )}
                {page.status === 'error' && (
                  <XCircle className="h-6 w-6 text-pop-fire drop-shadow" strokeWidth={2.5} />
                )}
              </div>
              <span className="absolute bottom-0 right-0 bg-pop-silhouette text-pop-cream text-[10px] font-bold px-1">
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Token expired error */}
      {tokenDead && (
        <p className="text-sm text-pop-fire text-center max-w-xs">
          Kód vypršel. Obnov QR kód na počítači a naskenuj znovu.
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={onPick}
      />

      {/* Action buttons */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {!tokenDead && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            className="pixel-btn flex items-center justify-center gap-2 text-lg px-6 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" strokeWidth={3} />
                Odesílám…
              </>
            ) : pages.length === 0 ? (
              <>
                <Camera className="h-6 w-6" strokeWidth={2.5} />
                Otevřít fotoaparát
              </>
            ) : (
              <>
                <Plus className="h-6 w-6" strokeWidth={2.5} />
                Přidat další stranu
              </>
            )}
          </button>
        )}

        {hasDone && (
          <button
            onClick={() => finish.mutate(undefined, { onSettled: () => setFinished(true) })}
            disabled={isUploading || finish.isPending}
            className="pixel-btn-secondary flex items-center justify-center gap-2 text-lg px-6 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {finish.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} />
            ) : (
              <CheckCircle2 className="h-5 w-5" strokeWidth={2.5} />
            )}
            Hotovo
          </button>
        )}
      </div>
    </PhoneFrame>
  )
}

function pageLabel(count: number): string {
  if (count === 1) return 'strana'
  if (count >= 2 && count <= 4) return 'strany'
  return 'stran'
}

function PhoneFrame({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 py-10 bg-pop-cream text-pop-silhouette">
      {children}
    </main>
  )
}

function StatusBlock({
  icon,
  title,
  message,
}: {
  icon: ReactNode
  title: string
  message: string
}) {
  return (
    <div className="flex flex-col items-center gap-4 text-center max-w-xs">
      <div className="bg-pop-cream border-[3px] border-pop-silhouette p-4">{icon}</div>
      <h1 className="text-2xl font-semibold uppercase tracking-wide">{title}</h1>
      <p className="text-sm text-pop-shadow">{message}</p>
    </div>
  )
}
